/**
 * The rep's own products and their key messages, projected to the {id, name}
 * shape the detector is grounded with and the resolver matches against. Pure and
 * Angular-free: callers pass the offline brands; this shapes and renders them.
 *
 * Key messages are paraphrased heavily in speech, so the detector is shown this
 * catalog and asked to return the exact id it picked. The app still validates
 * that id against the catalog, so a mis-copied id degrades to a name match rather
 * than a wrong record.
 */

export interface CatalogProduct {
  id: string;
  name: string;
}

export interface CatalogKeyMessage {
  id: string;
  name: string;
  productId: string;
  productName: string;
}

export interface DictationCatalog {
  products: CatalogProduct[];
  keyMessages: CatalogKeyMessage[];
}

/** Minimal brand shape the catalog needs; the real {@link Brand} class satisfies it. */
export interface CatalogBrand {
  ID: string;
  name: string;
  keyMessages: { ID: string; name: string }[];
}

export function buildDictationCatalog(brands: CatalogBrand[]): DictationCatalog {
  const products: CatalogProduct[] = [];
  const keyMessages: CatalogKeyMessage[] = [];
  const seenProducts = new Set<string>();
  for (const brand of brands ?? []) {
    const id = (brand?.ID ?? '').trim();
    const name = (brand?.name ?? '').trim();
    if (!id || !name || seenProducts.has(id)) continue;
    seenProducts.add(id);
    products.push({ id, name });
    // One entry per product a key message belongs to. A key message carried by
    // several products would otherwise keep whichever product it was first seen
    // under, and that product is attached to the meeting when the key message
    // matches — so a single entry makes the meeting name a product the rep may
    // never have discussed.
    const seenHere = new Set<string>();
    for (const km of brand.keyMessages ?? []) {
      const kmId = (km?.ID ?? '').trim();
      const kmName = (km?.name ?? '').trim();
      if (!kmId || !kmName || seenHere.has(kmId)) continue;
      seenHere.add(kmId);
      keyMessages.push({ id: kmId, name: kmName, productId: id, productName: name });
    }
  }
  return { products, keyMessages };
}

/**
 * Render the catalog as the reference block appended to the detection prompt.
 * Empty when there is nothing to ground against, so the prompt drops the section
 * entirely and the detector falls back to free-text names.
 */
export function renderCatalogForPrompt(catalog: DictationCatalog): string {
  if (!catalog.products.length && !catalog.keyMessages.length) {
    return '';
  }
  const byProduct = new Map<string, string[]>();
  for (const km of catalog.keyMessages) {
    const lines = byProduct.get(km.productId) ?? [];
    lines.push(`    - id=${km.id} name="${km.name}"`);
    byProduct.set(km.productId, lines);
  }
  const blocks = catalog.products.map((p) => {
    const kms = byProduct.get(p.id) ?? [];
    return [`- id=${p.id} name="${p.name}"`, ...kms].join('\n');
  });
  return blocks.join('\n');
}
